import { useState, useEffect } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { Sidebar } from "@/components/sidebar"
import { Button } from "@/components/ui/button"
import { PanelLeft, X } from "lucide-react"
import { Separator } from "@/components/ui/separator"
import { ThemeToggle } from "@/components/theme-toggle"
import { BillingTrialBadge } from "@/components/billing-trial-badge"
import { cn } from "@/lib/utils"
import { useOrganization } from "@/contexts/organization-context"
import { useLocalStorage } from "@/hooks/useLocalStorage"

// Define the page titles for breadcrumb (aligned with sidebar labels)
const getPageTitle = (pathname: string): string => {
  if (pathname === '/organizations') return 'Organizations'
  if (pathname.endsWith('/optimizer')) return 'Optimizer'
  if (pathname.endsWith('/edge')) return 'Scenarios'
  if (pathname.endsWith('/reports')) return 'Reports'
  if (pathname.endsWith('/analysis') || pathname.includes('/analysis/')) return 'Analysis'
  if (pathname.endsWith('/normalizations')) return 'Normalizations'
  if (pathname.endsWith('/integrations') || pathname.includes('/integrations/')) return 'Integrations'
  if (pathname.endsWith('/model')) return 'Model'
  if (pathname.endsWith('/monitoring')) return 'Monitoring'
  if (pathname.endsWith('/agents') || pathname.includes('/agents/')) return 'Agents'
  if (pathname.endsWith('/settings')) return 'Settings'
  if (pathname.endsWith('/guardian')) return 'Guardian'
  if (pathname.includes('/admin/apps-sdk')) return 'Apps SDK'
  if (pathname.endsWith('/help')) return 'Help'
  if (pathname.endsWith('/account')) return 'Account'
  return 'Dashboard'
}

export function DashboardLayout() {
  // Use localStorage to persist sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage('sidebarCollapsed', false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()

  // Get current page title for breadcrumb
  const currentPageTitle = getPageTitle(location.pathname)

  // Persist last visited page in localStorage
  useEffect(() => {
    // Only save pages within an organization (not /organizations or other top-level pages)
    if (location.pathname !== '/' && location.pathname !== '/organizations' && !location.pathname.includes('/oauth')) {
      localStorage.setItem('lastVisitedPage', location.pathname)
    }
  }, [location.pathname])

  const handleNavigate = (path: string) => {
    navigate(path)
  }

  return (
    <div className="flex min-h-screen bg-muted/40 overflow-hidden">
      {/* Sidebar wrapper - this creates the layout structure */}
      <div 
        className={cn(
          "group peer text-sidebar-foreground hidden md:block",
          sidebarCollapsed && "data-[collapsible=icon]"
        )}
        data-state={sidebarCollapsed ? "collapsed" : "expanded"}
        data-collapsible={sidebarCollapsed ? "icon" : ""}
        data-variant="inset"
        data-side="left"
      >
        {/* Spacer div that takes up sidebar width */}
        <div 
          className={cn(
            "relative bg-transparent transition-[width] duration-200 ease-linear",
            sidebarCollapsed ? "w-0" : "w-[calc(16rem+0.5rem)]"
          )}
        />
        
        {/* Fixed sidebar container */}
        <div 
          className={cn(
            "fixed inset-y-0 z-10 hidden h-svh transition-[left,right,width] duration-200 ease-linear md:flex p-2",
            sidebarCollapsed ? "left-[calc(var(--sidebar-width)*-1)] w-[calc(var(--sidebar-width)+0.5rem)]" : "left-0 w-[calc(var(--sidebar-width)+0.5rem)]"
          )}
        >
          <div className="flex h-full w-full flex-col">
            <Sidebar 
              collapsed={sidebarCollapsed} 
              onNavigate={handleNavigate}
              currentPath={location.pathname}
            />
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-[16rem] bg-background p-2">
            <div className="flex h-full w-full flex-col">
              <Sidebar 
                collapsed={false} 
                onNavigate={(path) => {
                  handleNavigate(path)
                  setMobileSidebarOpen(false)
                }}
                currentPath={location.pathname}
              />
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="fixed right-4 top-4 z-50"
            onClick={() => setMobileSidebarOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Main content */}
      <main className="flex min-h-screen flex-1 flex-col overflow-hidden">
        {/* Inset rounded container */}
        <div className={cn(
          "flex h-full flex-1 flex-col bg-background overflow-hidden md:m-2 md:ml-0 md:rounded-xl md:border md:shadow",
          sidebarCollapsed && "md:ml-2"
        )}>
          <header className="flex h-14 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear">
            <div className="flex w-full items-center gap-2 px-4 lg:px-6">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 -ml-1"
                onClick={() => {
                  if (window.innerWidth >= 768) {
                    setSidebarCollapsed(!sidebarCollapsed)
                  } else {
                    setMobileSidebarOpen(!mobileSidebarOpen)
                  }
                }}
              >
                <PanelLeft className="h-4 w-4" />
                <span className="sr-only">Toggle Sidebar</span>
              </Button>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <h1 className="text-base font-medium">{currentPageTitle}</h1>
              <div className="ml-auto flex items-center gap-2">
                <BillingTrialBadge />
                {currentOrganization && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex"
                      onClick={() => {
                        sessionStorage.setItem('previousOrgPath', location.pathname)
                        navigate('/organizations')
                      }}
                    >
                      {currentOrganization.name}
                    </Button>
                    <Separator orientation="vertical" className="h-4" />
                  </>
                )}
                <ThemeToggle />
              </div>
            </div>
          </header>
          <div className="flex flex-1 flex-col p-4 lg:p-6 overflow-auto">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  )
}